import { Link } from "react-router-dom";
import Testimonials from "../components/Testimonials";
import { motion } from "framer-motion";
import { cases } from "../components/casesData.js";
import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../constants";
import TeamSection from "../components/TeamSection";

// Popular cases (existing ones)
const featuredCases = [
  {
    title: "Readdle – Documents App",
    subtitle: "Improving Document app with AI",
    description:
      "We created a RAG system for Readdle Documents app to help users easily search through many text files on their devices.",
    image: "/images/projects/readdle.jpg",
    link: "/cases",
  },
  {
    title: "Bowlero – AI Assistant",
    subtitle: "AI-driven customer support assistant",
    description:
      "We created an AI-assistant for Bowlero, using advanced tools like LLaMa2, LangChain, and Python.",
    image: "/images/projects/bowlero.jpg",
    link: "/cases",
  },
  {
    title: "AI Coding assistant",
    subtitle: "AI Coding assistant (VS Code extension)",
    description:
      "Smart VS Code extension designed to streamline the developer workflow with autonomous agent mode.",
    image: "/images/projects/ai_agent.gif",
    link: "/cases",
  },
];

const Home = () => {
  const [activeTab, setActiveTab] = useState("Popular");
  const [chatExpanded, setChatExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  // Typewriter effect state
  const hintPhrases = [
    "Let's build coffee marketplace",
    "Let's build personal assistant app",
    "Let's build enterprise RAG system",
  ];
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [charIndex, setCharIndex] = useState(0);

  // Typewriter effect
  useEffect(() => {
    if (chatExpanded) return; // Don't run typewriter when chat is expanded

    const currentPhrase = hintPhrases[currentPhraseIndex];

    const timeout = setTimeout(() => {
      if (isTyping) {
        if (charIndex < currentPhrase.length) {
          setCurrentText(currentPhrase.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          setTimeout(() => setIsTyping(false), 1500);
        }
      } else {
        if (charIndex > 0) {
          setCurrentText(currentPhrase.slice(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          setCurrentPhraseIndex((prev) =>
            prev === hintPhrases.length - 1 ? 0 : prev + 1
          );
          setIsTyping(true);
        }
      }
    }, isTyping ? 100 : 50);

    return () => clearTimeout(timeout);
  }, [charIndex, isTyping, currentPhraseIndex, chatExpanded, hintPhrases]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    if (!chatExpanded) setChatExpanded(true);

    const userMessage = { role: "user", content: input };
    setMessages((prev) => [...prev, userMessage]);

    const currentInput = input;
    setInput("");
    setLoading(true);

    try {
      let cid = conversationId;
      if (!cid) {
        const newConvRes = await fetch(API_ENDPOINTS.NEW_CONVERSATION, {
          referrerPolicy: "unsafe-url",
        });
        const newConvData = await newConvRes.json();
        cid = newConvData.conversation_id;
        setConversationId(cid);
      }

      const response = await fetch(API_ENDPOINTS.CHAT, {
        method: "POST",
        referrerPolicy: "unsafe-url",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: currentInput,
          conversation_id: cid,
          token: process.env.REACT_APP_KALLISTO_API_TOKEN,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const assistantReply = data.reply;

      if (data.conversation_id && !conversationId) {
        setConversationId(data.conversation_id);
      }

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: assistantReply, timestamp: Date.now() },
      ]);
    } catch (error) {
      console.error("API Error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `API Error: ${error.message}`,
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConversationId(null);
    setChatExpanded(false);
  };

  // Filter cases based on active tab
  const getFilteredCases = () => {
    switch (activeTab) {
      case "AI Projects":
        return cases.filter((c) => c.category === "ai").slice(0, 6);
      case "Mobile Apps":
        return cases.filter((c) => c.techStack.includes("Mobile")).slice(0, 6);
      case "Web Apps":
        return cases.filter((c) => c.techStack.includes("Web")).slice(0, 6);
      default:
        return featuredCases;
    }
  };

  const displayCases = getFilteredCases();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white">
              Build something <span className="text-gradient">💚 Kallisto</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Create apps and websites by chatting with AI
            </p>

            {/* Input Box / Chat Window */}
            <div className="max-w-2xl mx-auto mt-12">
              <motion.div
                className="lovable-card p-2"
                animate={{
                  height: chatExpanded ? "auto" : "auto",
                  minHeight: chatExpanded ? "400px" : "auto",
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Chat Header */}
                {chatExpanded && (
                  <div className="flex items-center justify-between mb-4 px-4 pt-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      💬 AI Assistant
                    </h3>
                    <button
                      onClick={clearChat}
                      className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded-lg transition-all text-sm"
                    >
                      Clear Chat
                    </button>
                  </div>
                )}

                {/* Chat Messages */}
                {chatExpanded && (
                  <div className="h-64 overflow-y-auto border border-gray-700 rounded-xl p-4 bg-gray-800 mb-4 mx-4 scrollbar-thin scrollbar-thumb-gray-600">
                    {messages.length === 0 && (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-gray-400 text-center">
                          <span className="text-2xl mb-2 block">💬</span>
                          Start a conversation with the AI assistant...
                        </p>
                      </div>
                    )}

                    {messages.map((m, i) => (
                      <div
                        key={i}
                        className={`mb-4 flex ${
                          m.role === "user" ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                            m.role === "user"
                              ? "bg-blue-600 text-white rounded-br-sm"
                              : m.error
                              ? "bg-red-900 text-red-200 border border-red-700 rounded-bl-sm"
                              : "bg-gray-700 text-gray-100 border border-gray-600 rounded-bl-sm"
                          }`}
                        >
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {m.content}
                          </p>
                          {m.timestamp && !m.error && (
                            <span className="text-xs opacity-60 block mt-2">
                              {new Date(m.timestamp).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {loading && (
                      <div className="flex justify-start mb-4">
                        <div className="bg-gray-700 border border-gray-600 rounded-2xl rounded-bl-sm px-4 py-3">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: "0.1s" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: "0.2s" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Input Area */}
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 w-full px-4">
                  {!chatExpanded && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <span>💡</span>
                      <span className="text-sm">Public</span>
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder={
                      chatExpanded
                        ? "Type your message here..."
                        : currentText + (isTyping ? "|" : "")
                    }
                    className="flex-1 bg-transparent text-white placeholder-gray-400 outline-none text-lg py-4"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                  />

                  <button
                    className="bg-white text-gray-900 p-3 rounded-lg hover:bg-gray-100 transition-colors disabled:bg-white disabled:cursor-not-allowed w-full md:w-auto"
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    ) : (
                      <span className="text-xl">→</span>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

    {/* From the Community Section */}
    <section className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="lovable-card p-8 mb-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white">From our Portfolio</h2>
            <Link to="/cases" className="text-gray-300 hover:text-white transition-colors">
              View All →
            </Link>
          </div>
          
          {/* Category Tabs */}
          <div className="flex gap-6 mb-8 text-sm">
            {['Popular', 'AI Projects', 'Mobile Apps', 'Web Apps'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-2 transition-colors ${
                  activeTab === tab
                    ? 'text-white border-b-2 border-green-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Project Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayCases.map((project, index) => (
              <Link to="/cases" key={`${activeTab}-${index}`}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gray-900/50 rounded-xl overflow-hidden hover:bg-gray-900/70 transition-all group"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-green-500 rounded-full"></div>
                      <span className="text-sm text-white/70">{project.category === 'ai' ? 'AI Project' : 'Development'}</span>
                    </div>
                    <h3 className="text-white font-medium mb-2 group-hover:text-green-300 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-sm text-white/70 line-clamp-2">
                      {project.description}
                    </p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Services Section */}
    <section className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">Our Expertise</h2>
          <p className="text-xl text-white/80">From idea to deployment, we've got you covered</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: 'Full Stack Development',
              desc: 'End-to-end web application development with modern technologies and best practices.',
              icon: '🚀'
            },
            {
              title: 'Mobile Development',
              desc: 'Native and cross-platform mobile apps for iOS and Android using latest frameworks.',
              icon: '📱'
            },
            {
              title: 'DevOps & Cloud',
              desc: 'Scalable infrastructure, CI/CD pipelines, and cloud optimization for your applications.',
              icon: '☁️'
            },
            {
              title: 'AI & Machine Learning',
              desc: 'Intelligent solutions using computer vision, NLP, and deep learning technologies.',
              icon: '🤖'
            },
          ].map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="lovable-card p-6 hover:bg-gray-800/90 transition-all group"
            >
              <div className="text-3xl mb-4">{service.icon}</div>
              <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-green-300 transition-colors">
                {service.title}
              </h3>
              <p className="text-sm text-white/70 leading-relaxed">
                {service.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <TeamSection />

    {/* Contact Section */}
    <section className="py-20 px-4">
      <div className="max-w-4xl mx-auto text-center">
        <div className="lovable-card p-12">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to build something amazing?</h2>
          <p className="text-xl text-white/80 mb-8">
            Let's turn your idea into reality
          </p>
          <div className="space-y-4 text-white/70">
            <p>
              <span className="text-green-400">✉️</span>{' '}
              <a
                href="mailto:sergii.kozyrev@kallisto.dev"
                className="hover:text-white transition-colors"
              >
                sergii.kozyrev@kallisto.dev
              </a>
            </p>
            <p>
              <span className="text-green-400">📞</span> +61 45 227 3962
            </p>
            <p>
              <span className="text-green-400">📍</span> Sydney, Australia
            </p>
          </div>
          <div className="mt-8">
            <Link to="/contact" className="lovable-button">
              Get in touch
            </Link>
          </div>
        </div>
      </div>
    </section>

    <Testimonials />
  </div>
);
};

export default Home;
